import React, { useState, useEffect } from "react";
import style from "./bankDetails.module.css";
import CryptoJS from "crypto-js";
import axios from "axios";

import { ErrorToast, SuccessToast } from "../../../components/toastMessage";
import { BASE_URL } from "../../../constants/Constants";
import { useNavigate } from "react-router-dom";
import BoxComponent from "../../../components/CommonComponents/BoxComponent/BoxComponent";
import { _axios } from "../../../utils/axios";

const BankDetails = () => {
  // State for existence data
  const [existenceData, setExistenceData] = useState({ exists: false });
  const navigate = useNavigate();

  interface BankDetailsState {
    account_holder_name: string;
    account_no: string;
    ifs_code: string;
    bank_name: string;
    branch_name: string;
    account_type: string;
  }

  const [bankDetails, setBankDetails] = useState<BankDetailsState>({
    account_holder_name: "",
    account_no: "",
    ifs_code: "",
    bank_name: "",
    branch_name: "",
    account_type: "",
  });

  const [showPassword, setShowPassword] = useState(false);

  // Function to toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  // Function to encrypt data
  const encryptData = (data: {
    account_holder_name: any;
    account_no: string;
    account_type?: string;
    ifs_code: any;
    bank_name: any;
    branch_name: any;
  }) => {
    const encryptedData = { ...data };
    encryptedData.account_holder_name = CryptoJS.AES.encrypt(
      data.account_holder_name,
      "encryptionKey"
    ).toString();

    encryptedData.account_no = CryptoJS.AES.encrypt(
      data.account_no,
      "encryptionKey"
    ).toString();
    encryptedData.account_type = CryptoJS.AES.encrypt(
      data.account_type || "", // Encrypt with a default empty string if account_type is not provided
      "encryptionKey"
    ).toString();
    encryptedData.ifs_code = CryptoJS.AES.encrypt(
      data.ifs_code,
      "encryptionKey"
    ).toString();
    encryptedData.bank_name = CryptoJS.AES.encrypt(
      data.bank_name,
      "encryptionKey"
    ).toString();
    encryptedData.branch_name = CryptoJS.AES.encrypt(
      data.branch_name,
      "encryptionKey"
    ).toString();
    return encryptedData;
  };



  const decryptData = (data: {
    account_holder_name: string | CryptoJS.lib.CipherParams;
    account_no: string | CryptoJS.lib.CipherParams;
    ifs_code: string | CryptoJS.lib.CipherParams;
    bank_name: string | CryptoJS.lib.CipherParams;
    branch_name: string | CryptoJS.lib.CipherParams;
    account_type?: string; // Make account_type optional
  }) => {
    const decryptedData = { ...data };

    if (typeof data.account_holder_name !== "string") {
      decryptedData.account_holder_name = CryptoJS.AES.decrypt(
        data.account_holder_name,
        "encryptionKey"
      ).toString(CryptoJS.enc.Utf8);
    }

    decryptedData.account_holder_name = CryptoJS.AES.decrypt(
      data.account_holder_name,
      "encryptionKey"
    ).toString(CryptoJS.enc.Utf8);
    decryptedData.account_no = CryptoJS.AES.decrypt(
      data.account_no,
      "encryptionKey"
    ).toString(CryptoJS.enc.Utf8);
    decryptedData.account_type = data.account_type ? CryptoJS.AES.decrypt(
      data.account_type,
      "encryptionKey"
    ).toString(CryptoJS.enc.Utf8) : "";
    decryptedData.ifs_code = CryptoJS.AES.decrypt(
      data.ifs_code,
      "encryptionKey"
    ).toString(CryptoJS.enc.Utf8);
    decryptedData.bank_name = CryptoJS.AES.decrypt(
      data.bank_name,
      "encryptionKey"
    ).toString(CryptoJS.enc.Utf8);
    decryptedData.branch_name = CryptoJS.AES.decrypt(
      data.branch_name,
      "encryptionKey"
    ).toString(CryptoJS.enc.Utf8);

    // Provide a default value for account_type if it's missing
    if (!decryptedData.account_type) {
      decryptedData.account_type = ""; // Set it to an empty string
    }

    return {
      ...decryptedData,
      account_holder_name: String(decryptedData.account_holder_name),
      account_no: String(decryptedData.account_no),
      account_type: String(decryptedData.account_type),
      ifs_code: String(decryptedData.ifs_code),
      bank_name: String(decryptedData.bank_name),
      branch_name: String(decryptedData.branch_name),
    };
  };


  // Function to fetch bank details
  const fetchBankDetails = async () => {
    try {
      const existenceResponse = await _axios.get(
        `${BASE_URL}/api/bankDetails/checkExistence`
      );
      setExistenceData(existenceResponse.data.exists);

      if (existenceResponse.data.exists) {
        const bankDetailsResponse = await _axios.get(
          `${BASE_URL}/api/bankDetails/getBankDetails`
        );
        const decryptedBankDetails = decryptData(
          bankDetailsResponse.data.result
        );

        // Provide a default value for account_type
        const updatedBankDetails = {
          ...decryptedBankDetails,
          account_type: decryptedBankDetails.account_type || "",
        };

        setBankDetails(updatedBankDetails);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch bank details on component mount
  useEffect(() => {
    fetchBankDetails();
  }, [existenceData]);

  // Function to handle input change
  // const handleChange = (event: { target: { name: any; value: any } }) => {
  //   const { name, value } = event.target;
  //   setBankDetails({ ...bankDetails, [name]: value });
  // };

  const handleChange = (event: { target: { name: any; value: any } }) => {
    const { name, value } = event.target;
    if (name === 'account_type') {
      setBankDetails({ ...bankDetails, account_type: value });
    } else {
      setBankDetails({ ...bankDetails, [name]: value });
    }
  };

  // Function to handle form submission
  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();

    // Check if mandatory fields are filled
    if (!bankDetails.account_holder_name || !bankDetails.ifs_code || !bankDetails.account_no || !bankDetails.ifs_code || !bankDetails.account_type || !bankDetails.branch_name || !bankDetails.bank_name) {
      ErrorToast('All fields are mandatory');
      return;
    }

    try {
      const encryptedBankDetails = encryptData(bankDetails);
      const existenceResponse = await _axios.get(
        `${BASE_URL}/api/bankDetails/checkExistence`
      );
      setExistenceData({ exists: true });

      if (!existenceResponse.data.exists) {
        await _axios.post(
          `${BASE_URL}/api/bankDetails/create`,
          encryptedBankDetails
        );
        SuccessToast("Bank Details created successfully");
      } else {
        await _axios.put(
          `${BASE_URL}/api/bankDetails/edit`,
          encryptedBankDetails
        );
        SuccessToast("Bank Details updated successfully");
      }
      setTimeout(() => {
        window.location.reload();
      }, 1200);
    } catch (error) {
      console.error(error);
    }


  };

  return (
    <div>
      <div className={`${style.body} p-8`}>
        <p className={style.bodyTitleNormal}>Bank Details</p>
        <BoxComponent>
          <div className={style.bodyContanier}>
            <div className={style.logoUploadContainer}>
              <div className={style.uploadsection}>
                <div className="appIdForm">
                  {/* <p className={style.bodyTitleNormal}>Bank Details</p> */}
                  <form onSubmit={handleSubmit}>
                    <div className={style.formRow}>
                      <div className={style.formGroup}>
                        <label className={style.subHeadingLabel}>
                          Account Holder Name <span className="ml-1 text-danger"> *</span>
                        </label>
                        <input
                          className={style.InputTxtNum}
                          type="text"
                          name="account_holder_name"
                          value={bankDetails.account_holder_name}
                          onChange={handleChange}
                        />
                      </div>
                      <div className={style.formGroup}>
                        <label className={style.subHeadingLabel}>Account No
                          <span className="ml-1 text-danger"> *</span>
                        </label>
                        <div className={style.passwordContainer}>
                          <input
                            className={style.InputTxtNum}
                            type={showPassword ? "text" : "password"}
                            name="account_no"
                            value={bankDetails.account_no}
                            onChange={handleChange}
                            onKeyPress={(e) => {
                              // Allow only numbers and certain control keys like delete, backspace, etc.
                              const allowedChars = /[0-9]/;
                              if (!allowedChars.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                e.preventDefault();
                              }
                            }}
                          />
                          <span
                            className={style.togglePassword}
                            onClick={togglePasswordVisibility}
                          >
                            {showPassword ? (
                              <i className="fa fa-eye-slash"></i>
                            ) : (
                              <i className="fa fa-eye"></i>
                            )}
                          </span>
                        </div>
                      </div>

                    </div>
                    <div className={style.formRow}>
                      <div className={style.formGroup}>
                        <label className={style.subHeadingLabel}>IFSC
                          <span className="ml-1 text-danger"> *</span>
                        </label>
                        <input
                          className={style.InputTxtNum}
                          type="text"
                          name="ifs_code"
                          value={bankDetails.ifs_code}
                          onChange={handleChange}
                        />
                      </div>
                      <div className={style.formGroup}>
                        <label className={style.subHeadingLabel}>
                          Account Type <span className="ml-1 text-danger"> *</span>
                        </label>

                        <select
                          name="account_type"
                          className={style.InputTxtNumSelect}
                          value={bankDetails.account_type}
                          onChange={handleChange}
                        >
                          <option value="" disabled>
                            Select Account Type
                          </option>
                          <option value="Savings Account">
                            Savings Account
                          </option>
                          <option value="Current Account">
                            Current Account
                          </option>
                        </select>

                      </div>

                    </div>
                    <div className={style.formRow}>

                      <div className={style.formGroup}>
                        <label className={style.subHeadingLabel}>Branch Name
                          <span className="ml-1 text-danger"> *</span>
                        </label>
                        <input
                          className={style.InputTxtNum}
                          type="text"
                          name="branch_name"
                          value={bankDetails.branch_name}
                          onChange={handleChange}
                        />

                      </div>
                      <div className={style.formGroup}>
                        <label className={style.subHeadingLabel}>Bank Name
                          <span className="ml-1 text-danger"> *</span>
                        </label>
                        <input
                          className={style.InputTxtNum}
                          type="text"
                          name="bank_name"
                          value={bankDetails.bank_name}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className={style.buttonSection}>
                      <button className="bg-primary ml-4 px-6 py-2 font-bold text-white rounded-full disabled:bg-gray-300">
                        {existenceData ? "Save" : "Save"}
                      </button>
                      <button className="bg-black ml-4 px-6 py-2 font-bold text-white rounded-full disabled:bg-gray-300" onClick={() => navigate('/dashboard')}>Close</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </BoxComponent>
      </div>
    </div>
  );
};

export default BankDetails;
